import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import { Worksheet, CreateWorksheetData } from '../../types/worksheet';
import {
  ClipboardDocumentListIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  TruckIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useCustomers } from '../../hooks/useCustomers';
import { useLanguage } from '../../contexts/LanguageContext';
import { Combobox } from '@headlessui/react';

interface WorksheetFormProps {
  onSubmit: (data: CreateWorksheetData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Worksheet | null;
}

interface WorksheetFormData {
  title: string;
  description?: string;
  customerId: string;
  workType: 'installation' | 'maintenance' | 'repair' | 'inspection' | 'other';
  priority: 'low' | 'medium' | 'high';
  
  // Dátumok és időzítés
  dueDate?: Date | null;
  worksheetDate?: Date | null;
  startTime?: string;
  endTime?: string;
  
  // Kiszállási adatok
  deploymentOccurred?: boolean;
  deploymentDate?: Date | null;
  deploymentTime?: string;
  
  // Utazási adatok
  travelKmStart?: number;
  travelKmEnd?: number;
  travelKmTotal?: number;
  travelCosts?: number;
  
  // Munka részletei
  estimatedHours?: number;
  location?: string;
  workDetails?: string;
  materials?: string;
  equipment?: string;
  safetyNotes?: string;
  
  // Egyéb
  specialNotes?: string;
}

const WorksheetForm: React.FC<WorksheetFormProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { customers } = useCustomers();
  const { t } = useLanguage();
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [customerQuery, setCustomerQuery] = useState<string>('');

  const defaultValues: WorksheetFormData = {
    title: '',
    description: '',
    customerId: '',
    workType: 'installation',
    priority: 'medium',
    
    // Dátumok és időzítés
    dueDate: null,
    worksheetDate: null,
    startTime: '',
    endTime: '',
    
    // Kiszállási adatok
    deploymentOccurred: false,
    deploymentDate: null,
    deploymentTime: '',
    
    // Utazási adatok
    travelKmStart: 0,
    travelKmEnd: 0,
    travelKmTotal: 0,
    travelCosts: 0,
    
    // Munka részletei
    estimatedHours: 0,
    location: '',
    workDetails: '',
    materials: '',
    equipment: '',
    safetyNotes: '',
    
    // Egyéb
    specialNotes: ''
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<WorksheetFormData>({
    defaultValues: initialData || defaultValues
  });

  // Register customerId field for validation
  React.useEffect(() => {
    register('customerId', { required: true });
  }, [register]);

  // Update form when customer is selected
  React.useEffect(() => {
    setValue('customerId', selectedCustomer);
  }, [selectedCustomer, setValue]);

  useEffect(() => {
    if (isOpen && initialData) {
      // Convert date to proper format for form
      const formData = {
        ...initialData,
        dueDate: initialData.dueDate ? new Date(initialData.dueDate) : null
      };
      reset(formData);
      setSelectedCustomer(initialData.customerId);
    } else if (isOpen && !initialData) {
      reset(defaultValues);
      setSelectedCustomer('');
      setCustomerQuery('');
    }
  }, [initialData, reset, isOpen]);

  const filteredCustomers = customers.filter(customer => {
    if (!customerQuery.trim()) return true;
    
    const query = customerQuery.toLowerCase();
    const nameToMatch = customer.type === 'organization'
      ? customer.companyName.toLowerCase()
      : `${customer.lastName} ${customer.firstName}`.toLowerCase();
    
    return nameToMatch.includes(query) || 
           customer.email?.toLowerCase().includes(query) ||
           customer.phone?.toLowerCase().includes(query);
  });

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'maintenance': return <CogIcon className="w-5 h-5" />;
      case 'repair': return <WrenchScrewdriverIcon className="w-5 h-5" />;
      case 'inspection': return <MagnifyingGlassIcon className="w-5 h-5" />;
      default: return <ClipboardDocumentListIcon className="w-5 h-5" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'medium': return <ClockIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
      case 'low': return <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
      default: return <ClockIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const onSubmitHandler = (data: WorksheetFormData) => {
    const submitData: CreateWorksheetData = {
      ...data,
      customerId: selectedCustomer,
      status: 'draft',
      dueDate: data.dueDate || undefined,
      worksheetDate: data.worksheetDate || undefined,
      deploymentDate: data.deploymentDate || undefined,
      deploymentOccurred: data.deploymentOccurred || false,
      startTime: data.startTime || '',
      endTime: data.endTime || '',
      deploymentTime: data.deploymentTime || '',
      travelKmStart: data.travelKmStart || undefined,
      travelKmEnd: data.travelKmEnd || undefined,
      travelKmTotal: data.travelKmTotal || undefined,
      travelCosts: data.travelCosts || undefined,
      specialNotes: data.specialNotes || '',
    };
    onSubmit(submitData);
  };

  const handleModalClose = () => {
    reset(defaultValues);
    setSelectedCustomer('');
    setCustomerQuery('');
    onCancel();
  };

  const formatDateForInput = (date: Date | null | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={initialData ? t('worksheets.edit') : t('worksheets.new')}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6 p-6">
        
        {/* Basic Worksheet Info Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('worksheets.fields.title')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.title')} *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: true })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.title')}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{t('common.validation.required')}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.description')}
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.description')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Selection Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <UserIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('worksheets.fields.customer')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.customer')} *
                  </label>
                  
                  <Combobox value={selectedCustomer} onChange={setSelectedCustomer}>
                    <div className="relative">
                      <Combobox.Input
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          return customer ? (
                            customer.type === 'organization' 
                              ? customer.companyName 
                              : `${customer.firstName} ${customer.lastName}`
                          ) : '';
                        }}
                        onChange={(event) => setCustomerQuery(event.target.value)}
                        placeholder={t('common.placeholders.selectCustomer')}
                      />
                      <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {filteredCustomers.length === 0 && customerQuery !== '' ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                            {t('common.noData')}
                          </div>
                        ) : (
                          filteredCustomers.map((customer) => (
                            <Combobox.Option
                              key={customer.id}
                              value={customer.id}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                  active ? 'bg-primary-600 text-white' : 'text-gray-900 dark:text-gray-200'
                                }`
                              }
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 mr-3">
                                  {customer.type === 'organization' ? (
                                    <BuildingOffice2Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                  ) : (
                                    <UserIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                  )}
                                </div>
                                <div className="block truncate">
                                  {customer.type === 'organization' 
                                    ? customer.companyName 
                                    : `${customer.firstName} ${customer.lastName}`}
                                </div>
                              </div>
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                  {!selectedCustomer && errors.customerId && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{t('common.validation.required')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.location')}
                  </label>
                  <input
                    type="text"
                    {...register('location')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.location')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.estimatedHours')}
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    {...register('estimatedHours', { valueAsNumber: true })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Work Type and Priority Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <WrenchScrewdriverIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('worksheets.fields.workType')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.workType')} *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { value: 'installation', label: t('worksheets.workTypes.installation') },
                      { value: 'maintenance', label: t('worksheets.workTypes.maintenance') },
                      { value: 'repair', label: t('worksheets.workTypes.repair') },
                      { value: 'inspection', label: t('worksheets.workTypes.inspection') }
                    ].map((type) => (
                      <label key={type.value} className="relative flex items-center group">
                        <input
                          type="radio"
                          value={type.value}
                          {...register('workType')}
                          className="sr-only peer"
                        />
                        <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                      peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                      hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200 bg-white dark:bg-gray-700">
                          {getWorkTypeIcon(type.value)}
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-200 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                            {type.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.priority')}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: 'low', label: t('worksheets.priority.low') },
                      { value: 'medium', label: t('worksheets.priority.medium') },
                      { value: 'high', label: t('worksheets.priority.high') }
                    ].map((priority) => (
                      <label key={priority.value} className="relative flex items-center group">
                        <input
                          type="radio"
                          value={priority.value}
                          {...register('priority')}
                          className="sr-only peer"
                        />
                        <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                      peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                      hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200 bg-white dark:bg-gray-700">
                          {getPriorityIcon(priority.value)}
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-200 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                            {priority.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.dueDate')}
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(watch('dueDate'))}
                    onChange={(e) => setValue('dueDate', e.target.value ? new Date(e.target.value) : null)}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deployment and Travel Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/20 dark:to-blue-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <TruckIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Kiszállás és utazás</h3>
              
              <div className="space-y-4">
                {/* Worksheet Date and Times */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Munkalap dátuma
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(watch('worksheetDate'))}
                      onChange={(e) => setValue('worksheetDate', e.target.value ? new Date(e.target.value) : null)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Munka kezdete
                    </label>
                    <input
                      type="time"
                      {...register('startTime')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Munka vége
                    </label>
                    <input
                      type="time"
                      {...register('endTime')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Deployment Section */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('deploymentOccurred')}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-700"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Történt kiszállás</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kiszállás dátuma
                      </label>
                      <input
                        type="date"
                        value={formatDateForInput(watch('deploymentDate'))}
                        onChange={(e) => setValue('deploymentDate', e.target.value ? new Date(e.target.value) : null)}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Kiszállás időpontja
                      </label>
                      <input
                        type="time"
                        {...register('deploymentTime')}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Travel Data Section */}
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Km óra állás (indulás)
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmStart', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Km óra állás (érkezés)
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmEnd', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Összesen megtett km
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelKmTotal', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Utazási költség (Ft)
                      </label>
                      <input
                        type="number"
                        min="0"
                        {...register('travelCosts', { valueAsNumber: true })}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Work Details Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentTextIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('worksheets.fields.workDetails')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.workDetails')}
                  </label>
                  <textarea
                    {...register('workDetails')}
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.workDetails')}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('worksheets.fields.materials')}
                    </label>
                    <textarea
                      {...register('materials')}
                      rows={3}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('worksheets.fields.materials')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('worksheets.fields.equipment')}
                    </label>
                    <textarea
                      {...register('equipment')}
                      rows={3}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('worksheets.fields.equipment')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('worksheets.fields.safetyNotes')}
                  </label>
                  <textarea
                    {...register('safetyNotes')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder={t('worksheets.fields.safetyNotes')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-teal-100 to-green-100 dark:from-teal-900/20 dark:to-green-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentTextIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">További információk</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Különleges megjegyzések
                  </label>
                  <textarea
                    {...register('specialNotes')}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="További megjegyzések, észrevételek..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                     hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg
                     hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {initialData ? t('common.save') : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WorksheetForm; 